import { Component, OnInit, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-vendor-signup',
  templateUrl: './vendor-signup.component.html',
  styleUrls: ['./vendor-signup.component.scss']
})
export class VendorSignupComponent implements OnInit {
  vendorForm: FormGroup; // FormGroup for vendor information
  fields: any[] = []; // Fields data

  // Map field names to icons
  icons: { [key: string]: string } = {
    'Film Production': 'bi-camera-reels-fill',
    'Audio Production': 'bi-music-note-beamed',
    'Graphics & Design': 'bi-palette-fill',
    'Photography': 'bi-camera-fill',
    '3D Modeling': 'bi-box-fill' // Icon for 3D Modeling
  };

  // Map phase names to icons
  phaseIcons: { [key: string]: string } = {
    'Pre-Production': 'bi-lightbulb-fill',
    'Production': 'bi-camera-video-fill',
    'Post-Production': 'bi-film',
    'Core Photography Roles': 'bi-camera-fill',
    'Technical & Support': 'bi-gear-fill',
    'Photography Business & Management': 'bi-briefcase-fill',
    '3D Modeling & Asset Creation': 'bi-box-fill',
    'Texturing & Materials': 'bi-brush-fill',
    'Rigging & Animation': 'bi-gear-fill',
    'Lighting & Rendering': 'bi-brightness-high-fill',
    'Post-Production & Integration': 'bi-film'
  };

  selectedRoles: string[] = []; // Array to store selected roles
  uploadedFiles: { [roleName: string]: File[] } = {}; // Store uploaded files for each role
  uploadedFieldFiles: { [fieldName: string]: File[] } = {}; // Store uploaded files for each field

  @ViewChildren('fileInput') fileInputs!: QueryList<ElementRef>;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private route: ActivatedRoute // Inject ActivatedRoute to access the current route
  ) {
    // Initialize the form
    this.vendorForm = this.fb.group(
      {
        name: ['', Validators.required], // Required
        email: ['', [Validators.required, Validators.email]], // Required and validated
        phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]], // Required and validated
        agency: ['', Validators.required], // Required
        facebook: [''], // Optional
        twitter: [''], // Optional
        linkedin: [''], // Optional
        github: [''], // Added GitHub control
        instagram: [''], // Added Instagram control
        frameio: [''], // Added Frame.io control
        contractSigned: [false] // Checkbox for contract signed
      },
      {
        validators: [this.emailOrPhoneValidator] // Custom validator
      }
    );
  }

  ngOnInit(): void {
    // Check the current URL for specific keywords
    const isKultureKatta = this.route.snapshot.url.some((segment) =>
      segment.path.includes('kulture-katta')
    );
    const isModernSoftwareArchitecture = this.route.snapshot.url.some((segment) =>
      segment.path.includes('modern-software-architecture') // Updated keyword
    );

    // Load the appropriate JSON file based on the URL
    let jsonFile = '/assets/fields.json'; // Default JSON file
    if (isKultureKatta) {
      jsonFile = '/assets/kulture-katta.json';
    } else if (isModernSoftwareArchitecture) {
      jsonFile = '/assets/modern-software-architecture.json'; // Updated JSON file
    }

    this.http.get<any[]>(jsonFile).subscribe(
      (data) => {
        console.log('Loaded fields:', data); // Debugging: Log the loaded data
        this.fields = data;
      },
      (error) => {
        console.error('Error loading fields:', error);
      }
    );
  }

  // Custom validator to ensure either email or phone is provided
  emailOrPhoneValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const email = control.get('email')?.value;
    const phone = control.get('phone')?.value;

    if (!email && !phone) {
      return { emailOrPhoneRequired: true }; // Error if both are empty
    }
    return null; // Valid if at least one is provided
  }

  toggleRoleSelection(roleName: string): void {
    const index = this.selectedRoles.indexOf(roleName);
    if (index === -1) {
      this.selectedRoles.push(roleName); // Add role if not already selected
    } else {
      this.selectedRoles.splice(index, 1); // Remove role if already selected
    }
  }

  onFileSelected(event: Event, roleName: string): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files = Array.from(input.files); // Convert FileList to an array
      if (!this.uploadedFiles[roleName]) {
        this.uploadedFiles[roleName] = []; // Initialize array if not already present
      }
      this.uploadedFiles[roleName].push(...files); // Add selected files to the role's array
      console.log(`Files uploaded for ${roleName}:`, this.uploadedFiles[roleName]);
    }
  }

  onFieldFileSelected(event: Event, fieldName: string): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files = Array.from(input.files); // Convert FileList to an array
      if (!this.uploadedFieldFiles[fieldName]) {
        this.uploadedFieldFiles[fieldName] = []; // Initialize array if not already present
      }
      this.uploadedFieldFiles[fieldName].push(...files); // Add selected files to the field's array
      console.log(`Files uploaded for ${fieldName}:`, this.uploadedFieldFiles[fieldName]);
    }
  }

  triggerFileInput(roleName: string, event: Event): void {
    event.stopPropagation(); // Prevent the badge click event
    const fileInput = this.fileInputs.find((input) => input.nativeElement.getAttribute('role-name') === roleName);
    if (fileInput) {
      fileInput.nativeElement.click(); // Trigger the file input's click event
    }
  }

  onSubmit(): void {
    if (this.vendorForm.valid) {
      const formData = {
        ...this.vendorForm.value,
        selectedRoles: this.selectedRoles, // Include selected roles in the payload
        uploadedFiles: this.uploadedFiles, // Include uploaded files in the payload
      };

      // Store the data in local storage using the email as the key
      const email = this.vendorForm.get('email')?.value;
      if (email) {
        localStorage.setItem(email, JSON.stringify(formData));
        console.log(`Form data saved to local storage with key: ${email}`);
      } else {
        console.error('Email is required to save data to local storage.');
      }

      // Optionally, send the data to a mock API
      this.http.post('https://jsonplaceholder.typicode.com/posts', formData).subscribe(
        (response) => {
          console.log('Form submitted successfully:', response);
        },
        (error) => {
          console.error('Error submitting form:', error);
        }
      );
    } else {
      console.log('Form is invalid');
    }
  }
}
